/**
 * `fetch` for authenticated **api-v2** calls, wrapped so a 401/403 also nudges the single-session
 * guard. This is the third trigger from `bimgo-ai-web` PR #139 (alongside the Realtime broadcast
 * and the focus re-check in `useSessionGuard`): if an api-v2 request comes back unauthorized —
 * which is what a session revoked by a newer login looks like to the backend — re-verify against
 * GoTrue rather than waiting for the next broadcast or window focus.
 *
 * Safe to use as the default `fetch` for every api-v2 lib: `checkForRevokedSession` is throttled
 * (5s), de-duplicated, and **only signs out on a definitive GoTrue revocation** — a plain
 * unauthorized (expired token, a permission the backend refuses) just costs one throttled refresh
 * and never signs anyone out. So over-triggering is harmless; under-triggering loses the net.
 */
import { checkForRevokedSession } from "./authSession";

export const apiFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 401 || response.status === 403) {
    // Fire-and-forget: the caller still gets its response and decides what to render; the guard
    // decides, independently and against GoTrue, whether the session is actually gone.
    void checkForRevokedSession().catch(() => undefined);
  }
  return response;
};
